import {invariant} from '@raison/toolbox';
import React, {
    ReactElement,
    createContext,
    isValidElement,
    useCallback,
    useContext,
    useDebugValue,
    useRef,
    useState,
} from 'react';
import {useShallowMemo} from '../../Hooks';

export type MountControlChild = ReactElement | null | undefined | false;

export function isMountable(child: MountControlChild): child is ReactElement {
    return isValidElement(child);
}

function isSameInstance(a: ReactElement, b: ReactElement) {
    if (a === b) return true;
    return a.type === b.type && a.key == b.key;
}

/**
 * Allows the only child of this component to control when it is unmounted.
 * I'm to lazy to try to implement this for an array of children.
 *
 * Implements *out-then-in* strategy for switching between children.
 */
export function MountControl({children: newChild}: {children: MountControlChild}) {
    const [canUnmount, setCanUnmount] = useState(false);
    const allowUnmount = useCallback(() => {
        setCanUnmount(true);
    }, []);

    const elementToRender = useRef(newChild);

    let shouldUnmount = false;
    if (isMountable(newChild)) {
        if (!isMountable(elementToRender.current) || isSameInstance(newChild, elementToRender.current)) {
            elementToRender.current = newChild;
        } else {
            shouldUnmount = true;
        }
    } else {
        shouldUnmount = true;
    }

    if (shouldUnmount && canUnmount) {
        // shouldUnmount indicates that newChild is either of new type or is not mountable
        // canUnmount indicates that previous elementToRender is ready to be replaced
        elementToRender.current = newChild;
        setCanUnmount(false);
    }

    const controls = useShallowMemo({isMounted: !shouldUnmount, allowUnmount});

    return (
        <MountControlContext.Provider value={useCallback(() => controls, [controls])}>
            {elementToRender.current}
        </MountControlContext.Provider>
    );
}

const MountControlContext = createContext<
    () => {
        isMounted: boolean;
        allowUnmount: () => void;
    }
>(() => {
    invariant(false, "Can't use MountControl without provider");
});

export function useMountControl() {
    const value = useContext(MountControlContext)();
    useDebugValue(value, ({isMounted}) => `isMounted = ${isMounted}`);
    return value;
}
